import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import cn from "classnames";
import type { SidebarLink } from "./types";

interface Props {
  items: SidebarLink[];
  triggerRef: React.RefObject<HTMLElement | null>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const GAP = 8;

export default function SidebarFlyout({ items, triggerRef, onMouseEnter, onMouseLeave }: Props) {
  const { t } = useTranslation("Sidebar");
  const location = useLocation();
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [isPositioned, setIsPositioned] = useState(false);

  const updatePosition = useCallback(
    function updatePosition() {
      if (!triggerRef.current) return;

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const flyoutRect = flyoutRef.current?.getBoundingClientRect();
      const flyoutHeight = flyoutRect?.height || 0;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const flyoutWidth = flyoutRect?.width || 0;

      let left = triggerRect.right + GAP;
      let top = triggerRect.top;

      if (left + flyoutWidth > viewportWidth - GAP) {
        left = triggerRect.left - flyoutWidth - GAP;
      }

      top = Math.max(GAP, Math.min(top, viewportHeight - flyoutHeight - GAP));

      setPosition({ top, left });
    },
    [triggerRef],
  );

  useEffect(
    function positionEffect() {
      let aborted = false;
      let innerRafId: number | undefined;

      updatePosition();

      const outerRafId = requestAnimationFrame(() => {
        if (aborted) return;
        innerRafId = requestAnimationFrame(() => {
          if (aborted) return;
          updatePosition();
          setIsPositioned(true);
        });
      });

      function handleScrollOrResize() {
        updatePosition();
      }

      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);

      return () => {
        aborted = true;
        cancelAnimationFrame(outerRafId);
        if (innerRafId !== undefined) cancelAnimationFrame(innerRafId);
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    },
    [updatePosition],
  );

  const content = (
    <div
      ref={flyoutRef}
      className={cn(
        "fixed z-[10100] radius-md background-static-white p-1 shadow-[0_6px_12px_0_var(--color-alpha-black-05),0_1px_1px_0_var(--color-alpha-black-10),0_0_0_1px_var(--color-alpha-black-10)]",
        isPositioned && "animate-fadeIn",
      )}
      style={{
        top: position ? `${position.top}px` : 0,
        left: position ? `${position.left + 8}px` : 0,
        visibility: isPositioned ? "visible" : "hidden",
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      {items.map(function renderFlyoutItem(item) {
        const Icon = item.icon;
        const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + "/");

        return (
          <Link
            key={item.label}
            to={item.carryFrom ? `${item.href}?from=${encodeURIComponent(location.pathname)}` : item.href}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-md whitespace-nowrap transition-colors cursor-pointer",
              isActive
                ? "text-body-bold-sm content-action-brand"
                : "text-body-regular-sm content-base-primary hover:bg-grey-50 hover:dark:bg-grey-900",
            )}>
            {Icon && (
              <span className={cn("shrink-0", isActive ? "content-action-brand" : "content-base-secondary")}>
                <Icon size={16} variant={isActive ? "Bulk" : "Linear"} color="currentColor" />
              </span>
            )}
            <span className="text-body-regular-sm content-action-neutral">{t(item.label)}</span>
          </Link>
        );
      })}
    </div>
  );

  return createPortal(content, document.body);
}

